import { action, computed, makeObservable, observable } from 'mobx'

import { IAnalyticSettingsField } from '~/client/graph'
import AnalyticsSettingsStore from '~/client/src/desktop/stores/domain/AnalyticsSettings.store'
import AnalyticsReportsType from '~/client/src/shared/enums/AnalyticReportsType'
import Delivery from '~/client/src/shared/models/Delivery'
import IScannerHistoryPair from '~/client/src/shared/models/IScannerHistoryPair'
import Workflow from '~/client/src/shared/models/Workflow'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import DeliveryStore from '~/client/src/shared/stores/domain/Deliveries.store'
import ScanHistoriesStore from '~/client/src/shared/stores/domain/ScanHistories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import {
  ACTIVATE_PROJECT,
  LOAD_ANALYTICS_SETTING,
  LOAD_AND_LISTEN_TO_COMPANIES,
  LOAD_AND_LISTEN_TO_SCANNERS,
  LOAD_AND_LISTEN_TO_SCAN_HISTORIES,
  LOAD_AND_LISTEN_TO_USER_PROJECTS,
  LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES,
  LOAD_AND_LISTEN_TO_WORKFLOW_TYPES,
  LOAD_REPORT_TEMPLATE,
  RESET_ALL_FILTERS,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import ProjectDateStore, {
  areIntervalTimesIntersects,
} from '~/client/src/shared/stores/ui/ProjectDate.store'

import { IDateFilters } from '../../components/Filters/DateSelector/DateSelector.store'
import { TwoMonthsDatePickerMode } from '../../components/TwoMonthsDatePicker/TwoMonthsDatePicker.store'
import DesktopEventStore from '../../stores/EventStore/DesktopEvents.store'

export default class ReportsStore {
  @observable public isScanStationDialogDisplayed: boolean = false
  @observable public isDeliveryReportDialogDisplayed: boolean = false
  @observable public isFormReportDialogDisplayed: boolean = false
  @observable public selectedScanStation: IScannerHistoryPair = null
  @observable public selectedFormReportId: string = null
  @observable public selectedDeliveryReport: Delivery = null
  @observable public activeReportView: IAnalyticSettingsField = null
  @observable public dateFilters: IDateFilters = {
    startDate: null,
    daysToAdd: 0,
    isDateFilterActive: true,
  }
  @observable public selectedDatePickerMode: TwoMonthsDatePickerMode =
    TwoMonthsDatePickerMode.ONE_DAY
  @observable public workflows: Workflow[] = []

  public constructor(
    private readonly eventsStore: DesktopEventStore,
    private readonly scanHistoriesStore: ScanHistoriesStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
    private readonly analyticsSettingsStore: AnalyticsSettingsStore,
    private readonly deliveryStore: DeliveryStore,
    private readonly projectDateStore: ProjectDateStore,
  ) {
    makeObservable(this)
  }

  @action.bound
  public setWorkflows(workflows: Workflow[]) {
    this.workflows = workflows
  }

  @action.bound
  public setDefaultReportView() {
    this.activeReportView = this.dropdownFields?.[0]
  }

  @action.bound
  public activeReportViewChange(view: IAnalyticSettingsField) {
    this.activeReportView = view

    Promise.resolve().then(() => {
      this.eventsStore.dispatch(RESET_ALL_FILTERS)
    })
  }

  public loadReportTemplateFiles = (templateId: string) => {
    this.eventsStore.dispatch(LOAD_REPORT_TEMPLATE, templateId)
  }

  @action.bound
  public changeDatePickerMode(mode: TwoMonthsDatePickerMode): void {
    this.selectedDatePickerMode = mode
  }

  public get isDateFilterActive(): boolean {
    return this.dateFilters.isDateFilterActive
  }

  public get startDate(): Date {
    return this.isDateFilterActive
      ? this.dateFilters.startDate
      : this.projectDateStore.getMinAvailableDate()
  }

  public get endDate(): Date {
    return this.isDateFilterActive
      ? this.projectDateStore.getEndDate(this.dateFilters)
      : this.projectDateStore.getMaxAvailableDate()
  }

  private get availableNonFormReportTypes(): string[] {
    const types: string[] = [AnalyticsReportsType.SCAN_STATION_REPORT]

    if (!this.state.isDeliveriesDisabled) {
      types.push(AnalyticsReportsType.DELIVERY_REPORT)
    }

    return types
  }

  private get state() {
    return this.eventsStore.appState
  }

  @computed
  public get dropdownFields(): IAnalyticSettingsField[] {
    const nonFormReportTypes = this.availableNonFormReportTypes

    // Form fields are versioned: several saved entries can resolve to the same
    // current type. Group them by resolved type (first-seen order) and let
    // pickAuthoritativeField choose. Non-form fields pass through inline.
    const slots: Array<
      | { kind: 'form'; typeId: string }
      | { kind: 'nonform'; field: IAnalyticSettingsField }
    > = []
    const formGroups = new Map<
      string,
      { actualType: WorkflowType; fields: IAnalyticSettingsField[] }
    >()

    for (const field of this.analyticsSettingsStore.mySettingFields) {
      if (field.type === AnalyticsReportsType.FORM_REPORT) {
        const actualType = this.workflowTypesStore.getActualTypeByAnyVersionId(
          field.id,
        )
        if (!actualType) continue // workflow type deleted — exclude

        const group = formGroups.get(actualType.id)
        if (group) {
          group.fields.push(field)
        } else {
          formGroups.set(actualType.id, { actualType, fields: [field] })
          slots.push({ kind: 'form', typeId: actualType.id })
        }
      } else if (field.isEnabled && nonFormReportTypes.includes(field.type)) {
        slots.push({ kind: 'nonform', field })
      }
    }

    const result: IAnalyticSettingsField[] = []
    for (const slot of slots) {
      if (slot.kind === 'nonform') {
        result.push(slot.field)
        continue
      }

      const { actualType, fields } = formGroups.get(slot.typeId)
      const authoritative = this.pickAuthoritativeField(fields, actualType.id)

      // The isEnabled gate is applied to the authoritative entry, not up
      // front: a disabled current entry must hide the type even when an
      // orphaned old entry is still flagged enabled.
      if (authoritative.isEnabled) {
        result.push({
          ...authoritative,
          id: actualType.id,
          name: actualType.name,
        })
      }
    }

    return result
  }

  // When several saved analytics fields resolve to the same current workflow
  // type, the entry whose id already equals the current version is canonical
  // (it is the one the Analytics-setup UI reads and writes, so it holds the
  // up-to-date columns and enabled state). Fall back to the newest underlying
  // version when no current-id entry exists yet (type edited but analytics
  // settings not re-saved).
  private pickAuthoritativeField(
    fields: IAnalyticSettingsField[],
    currentTypeId: string,
  ): IAnalyticSettingsField {
    const currentEntry = fields.find(field => field.id === currentTypeId)
    if (currentEntry) return currentEntry

    return fields.reduce((newest, field) =>
      this.versionUpdatedAt(field.id) > this.versionUpdatedAt(newest.id)
        ? field
        : newest,
    )
  }

  private versionUpdatedAt(permitTypeId: string): number {
    return (
      this.workflowTypesStore.getPermitTypeById(permitTypeId)?.updatedAt || 0
    )
  }

  public areDatesWithinSelectedRange = (
    startDate: Date | number,
    endDate: Date | number,
    selectedStartDate: Date | number,
    selectedEndDate: Date | number,
  ): boolean => {
    const { startOfDay, endOfDay } = this.projectDateStore

    const selectedStart = startOfDay(selectedStartDate)
    const selectedEnd = endOfDay(selectedEndDate)

    return areIntervalTimesIntersects(
      {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      },
      {
        startDate: selectedStart,
        endDate: selectedEnd,
      },
    )
  }

  public get allScanStationHistory(): IScannerHistoryPair[] {
    return this.scanHistoriesStore.historiesByScannerPairs
  }

  public get scanStationHistoriesInPeriodInterval(): IScannerHistoryPair[] {
    return this.scanHistoriesStore.historiesByScannerPairs.filter(pair =>
      this.areDatesWithinSelectedRange(
        this.startDate,
        this.endDate,
        pair.history.date,
        pair.history.endDate || pair.history.date,
      ),
    )
  }

  public get formReportPermitsInPeriodInterval(): Workflow[] {
    const { startOfDay, endOfDay } = this.projectDateStore
    const startDate = startOfDay(this.startDate)
    const endDate = endOfDay(this.endDate)

    return this.allFormReportPermits.filter(form =>
      form.isScheduledWithinRange(startDate, endDate),
    )
  }

  public get deliveriesInPeriodInterval(): Delivery[] {
    const { startOfDay, endOfDay } = this.projectDateStore
    const startDate = startOfDay(this.startDate)
    const endDate = endOfDay(this.endDate)

    return this.allDeliveries.filter(delivery =>
      delivery.isScheduledWithinRange(startDate, endDate),
    )
  }

  public get allDeliveries(): Delivery[] {
    return this.deliveryStore.availableDeliveries
  }

  @computed
  public get allFormReportPermits(): Workflow[] {
    const activePermitType = this.workflowTypesStore.actualTypes.find(
      t => t.id === this.activeReportView.id,
    )

    if (!activePermitType) {
      return []
    }

    return this.workflows.filter(
      form =>
        form.getTypeOfPermitType(this.workflowTypesStore) ===
        activePermitType.type,
    )
  }

  public get isDataPrepared() {
    return !this.isLoading && this.isDataReceived
  }

  public get isScanStation() {
    return (
      this.activeReportView?.type === AnalyticsReportsType.SCAN_STATION_REPORT
    )
  }

  public get isFormReport() {
    return this.activeReportView?.type === AnalyticsReportsType.FORM_REPORT
  }

  public get isDeliveryReport() {
    return this.activeReportView?.type === AnalyticsReportsType.DELIVERY_REPORT
  }

  private get isLoading() {
    const { loading } = this.eventsStore.appState

    return (
      loading.get(ACTIVATE_PROJECT) ||
      loading.get(LOAD_AND_LISTEN_TO_SCAN_HISTORIES) ||
      loading.get(LOAD_AND_LISTEN_TO_SCANNERS) ||
      loading.get(LOAD_AND_LISTEN_TO_COMPANIES) ||
      loading.get(LOAD_AND_LISTEN_TO_USER_PROJECTS) ||
      loading.get(LOAD_AND_LISTEN_TO_WORKFLOW_TYPES) ||
      loading.get(LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES) ||
      loading.get(LOAD_ANALYTICS_SETTING)
    )
  }

  private get isDataReceived() {
    const stores = [
      this.scanHistoriesStore,
      this.workflowTypesStore,
      this.analyticsSettingsStore,
      this.deliveryStore,
    ]
    return stores.every(s => s.isDataReceived)
  }

  @action.bound
  public showScanStationReports(report: IScannerHistoryPair) {
    this.isScanStationDialogDisplayed = true
    this.selectedScanStation = report
  }

  @action.bound
  public closeScanStationReports() {
    this.isScanStationDialogDisplayed = false
    this.selectedScanStation = null
  }

  @action.bound
  public showFormReports(report: Workflow) {
    this.isFormReportDialogDisplayed = true
    this.selectedFormReportId = report.id
  }

  @action.bound
  public closeFormReports() {
    this.isFormReportDialogDisplayed = false
    this.selectedFormReportId = null
  }

  @action.bound
  public showDeliveryReports(delivery: Delivery) {
    this.isDeliveryReportDialogDisplayed = true
    this.selectedDeliveryReport = delivery
  }
  @action.bound
  public closeDeliveryReports() {
    this.isDeliveryReportDialogDisplayed = false
    this.selectedDeliveryReport = null
  }

  @computed
  public get selectedFormReport(): Workflow {
    return this.workflows.find(w => w.id === this.selectedFormReportId)?.copy()
  }
}
